import React, { useRef, useState, useEffect } from 'react';
import { processSvgForRender } from './svgUtils';
import { buildSVGGradient, buildGradientCss, hexToRgba } from './renderUtils';
import { StaticTextBlock, textVisualStyle, StaticQR, imageImgStyle, imageBoxRadius } from './staticTextRender';
import { getFrameClip } from './frameClip';
import { computeFrameImageLayout } from './frameLayout';
import { blendModeStyle } from './blendModes';
import { composeFilter, duotoneTableValues, shadowBoxCss } from './propertyEffects';
import { collectFontSpecs } from '../../lib/collectFonts';
import { waitForFontSpecs } from '../../lib/fonts';

/**
 * Three rendering modes:
 *
 * fillWidth=false (default)
 *   Renders at exact thumbWidth × thumbHeight px.
 *
 * fillWidth=true
 *   Stretches to 100% of parent, preserves real canvas aspect ratio.
 *
 * fillContainer=true
 *   Cover-scales to fill parent (parent must have explicit size + overflow:hidden).
 */
const PageThumbnailPreview = ({
  page,
  canvasSize,
  thumbWidth = 130,
  fillWidth = false,
  fillContainer = false,
  // Optional render window: union of canvas + element bounds, so elements
  // positioned outside canvas (bleed) remain visible. Defaults to canvas.
  viewBounds = null,
}) => {
  const containerRef = useRef(null);
  const [containerW, setContainerW] = useState(thumbWidth);
  const [containerH, setContainerH] = useState(thumbWidth);

  useEffect(() => {
    if (!fillWidth && !fillContainer) return;
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver(entries => {
      const { width, height } = entries[0]?.contentRect ?? {};
      if (width  > 0) setContainerW(width);
      if (height > 0) setContainerH(height);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [fillWidth, fillContainer]);

  // Load the exact font FACES this page uses (family+weight+style) before the
  // preview is considered font-ready. PageThumbnailPreview is the shared live
  // preview renderer (templates gallery, My Templates, dashboards, GridView),
  // so without this every preview surface renders text in the browser fallback
  // face — its different glyph metrics shift text width/wrapping/alignment vs
  // the editor. Keyed on a stable face signature so it only fires when the set
  // of faces changes.
  //
  // `fontsReady` does two things:
  //  1. RE-KEYS the scaled subtree below. Text that laid out during the
  //     fallback window keeps a line box computed from FALLBACK metrics, and
  //     Safari/WebKit does not reliably reflow it when the real face arrives —
  //     the first baseline stays anchored where the fallback put it, so every
  //     affected text block paints lower than the editor. Remounting the
  //     subtree once faces are loaded forces a fresh layout with the same
  //     metrics the editor used.
  //  2. Gates the GPU-layer promotion (`will-change: transform`). Promoting
  //     early rasterizes the compositor layer with fallback metrics, and some
  //     browsers never re-rasterize the cached layer (WebKit can even leave it
  //     blank). Because the re-key and the promotion flip together, the layer
  //     is built exactly once, from a fresh font-correct layout.
  const specs = collectFontSpecs([page]) || [];
  const fontSig = specs.map((s) => `${s.family}|${s.weight}|${s.style}`).sort().join(',');
  const [fontsReady, setFontsReady] = useState(false);
  useEffect(() => {
    let cancelled = false;
    if (!fontSig) { setFontsReady(true); return undefined; }
    setFontsReady(false);
    const sigSpecs = fontSig.split(',').map((k) => {
      const [family, weight, style] = k.split('|');
      return { family, weight, style };
    });
    (async () => {
      try {
        await waitForFontSpecs(sigSpecs);
      } catch { /* best effort — fall through and promote anyway */ }
      if (!cancelled) setFontsReady(true);
    })();
    return () => { cancelled = true; };
  }, [fontSig]);

  // Resolved view: explicit viewBounds or full canvas
  const view = viewBounds ?? { left: 0, top: 0, width: canvasSize.width, height: canvasSize.height };

  // ── Scale + offset ────────────────────────────────────────────────────────
  let scale, offsetX = 0, offsetY = 0;
  if (fillContainer) {
    const scaleX = containerW / view.width;
    const scaleY = containerH / view.height;
    scale = Math.max(scaleX, scaleY);
    offsetX = (containerW - view.width  * scale) / 2;
    offsetY = (containerH - view.height * scale) / 2;
  } else {
    const effectiveWidth = fillWidth ? containerW : thumbWidth;
    scale = effectiveWidth / view.width;
  }
  const thumbHeight = fillContainer ? containerH : Math.round(view.height * scale);

  // ── Clip-path geometry helper ─────────────────────────────────────────────
  // Mirror of CanvasEditor getMaskShapeSVGContent — all shape types supported.
  const getClipShape = (maskEl, contentEl) => {
    const ox = maskEl.x - contentEl.x;
    const oy = maskEl.y - contentEl.y;
    const w  = maskEl.width;
    const h  = maskEl.height;
    const rot = maskEl.rotation || 0;

    const wrap = (child) => {
      if (rot === 0) return child;
      return <g transform={`rotate(${rot} ${ox + w / 2} ${oy + h / 2})`}>{child}</g>;
    };

    let shape;
    if (maskEl.type === 'shape') {
      const cx = ox + w / 2, cy = oy + h / 2;
      switch (maskEl.shape) {
        case 'circle':
          shape = <ellipse cx={cx} cy={cy} rx={w / 2} ry={h / 2} />;
          break;
        case 'rect':
        case 'roundedRect': {
          const r = Math.min(maskEl.cornerRadius || (maskEl.shape === 'roundedRect' ? 12 : 0), w / 2, h / 2);
          shape = <rect x={ox} y={oy} width={w} height={h} rx={r} ry={r} />;
          break;
        }
        case 'triangle':
          shape = <polygon points={`${cx},${oy} ${ox},${oy + h} ${ox + w},${oy + h}`} />;
          break;
        case 'diamond':
          shape = <polygon points={`${cx},${oy} ${ox + w},${cy} ${cx},${oy + h} ${ox},${cy}`} />;
          break;
        case 'hexagon': {
          const r = Math.min(w, h) / 2;
          const pts = Array.from({ length: 6 }, (_, i) => {
            const a = (i * 60 - 90) * Math.PI / 180;
            return `${cx + r * Math.cos(a)},${cy + r * Math.sin(a)}`;
          }).join(' ');
          shape = <polygon points={pts} />;
          break;
        }
        case 'pentagon': {
          const r = Math.min(w, h) / 2;
          const pts = Array.from({ length: 5 }, (_, i) => {
            const a = (i * 72 - 90) * Math.PI / 180;
            return `${cx + r * Math.cos(a)},${cy + r * Math.sin(a)}`;
          }).join(' ');
          shape = <polygon points={pts} />;
          break;
        }
        case 'octagon': {
          const r = Math.min(w, h) / 2;
          const pts = Array.from({ length: 8 }, (_, i) => {
            const a = (i * 45 - 90 + 22.5) * Math.PI / 180;
            return `${cx + r * Math.cos(a)},${cy + r * Math.sin(a)}`;
          }).join(' ');
          shape = <polygon points={pts} />;
          break;
        }
        case 'star': {
          const outer = Math.min(w, h) / 2, inner = outer * 0.4;
          let d = '';
          for (let i = 0; i < 10; i++) {
            const r2 = i % 2 === 0 ? outer : inner;
            const a = (i * 36 - 90) * Math.PI / 180;
            d += (i === 0 ? 'M' : 'L') + `${cx + r2 * Math.cos(a)},${cy + r2 * Math.sin(a)}`;
          }
          shape = <path d={d + 'Z'} />;
          break;
        }
        case 'heart': {
          const d = `M${cx},${oy + h * 0.9} C${ox + w * 0.2},${oy + h * 0.7} ${ox + w * 0.05},${oy + h * 0.5} ${ox + w * 0.05},${oy + h * 0.3} C${ox + w * 0.05},${oy + h * 0.15} ${ox + w * 0.15},${oy + h * 0.05} ${ox + w * 0.275},${oy + h * 0.05} C${ox + w * 0.35},${oy + h * 0.05} ${ox + w * 0.42},${oy + h * 0.1} ${cx},${oy + h * 0.2} C${ox + w * 0.58},${oy + h * 0.1} ${ox + w * 0.65},${oy + h * 0.05} ${ox + w * 0.725},${oy + h * 0.05} C${ox + w * 0.85},${oy + h * 0.05} ${ox + w * 0.95},${oy + h * 0.15} ${ox + w * 0.95},${oy + h * 0.3} C${ox + w * 0.95},${oy + h * 0.5} ${ox + w * 0.8},${oy + h * 0.7} ${cx},${oy + h * 0.9}Z`;
          shape = <path d={d} />;
          break;
        }
        case 'arrow': {
          const pts = `${ox},${oy + h * 0.3} ${ox + w * 0.7},${oy + h * 0.3} ${ox + w * 0.7},${oy} ${ox + w},${cy} ${ox + w * 0.7},${oy + h} ${ox + w * 0.7},${oy + h * 0.7} ${ox},${oy + h * 0.7}`;
          shape = <polygon points={pts} />;
          break;
        }
        case 'cross': {
          const cw = w * 0.35, ch = h * 0.35;
          const pts = `${cx - cw / 2},${oy} ${cx + cw / 2},${oy} ${cx + cw / 2},${cy - ch / 2} ${ox + w},${cy - ch / 2} ${ox + w},${cy + ch / 2} ${cx + cw / 2},${cy + ch / 2} ${cx + cw / 2},${oy + h} ${cx - cw / 2},${oy + h} ${cx - cw / 2},${cy + ch / 2} ${ox},${cy + ch / 2} ${ox},${cy - ch / 2} ${cx - cw / 2},${cy - ch / 2}`;
          shape = <polygon points={pts} />;
          break;
        }
        case 'parallelogram': {
          const skewX = w * 0.25;
          shape = <polygon points={`${ox + skewX},${oy} ${ox + w},${oy} ${ox + w - skewX},${oy + h} ${ox},${oy + h}`} />;
          break;
        }
        default:
          shape = <rect x={ox} y={oy} width={w} height={h} />;
      }
    } else {
      // image / svg / text mask — rectangular clip
      shape = <rect x={ox} y={oy} width={w} height={h} />;
    }

    return wrap(shape);
  };

  // ── Element renderer ──────────────────────────────────────────────────────
  // Render every element; the page container's overflow:hidden does the
  // clipping. Partially-overlapping elements show their visible portion.
  const elements = page.elements || [];

  const renderThumbElement = (el) => {
    if (el.visible === false) return null;
    if (el.isMaskShape || el.isClipBase) return null;

    const maskEl = el.clippedTo ? elements.find(e => e.id === el.clippedTo) : null;
    const clipId  = maskEl ? `tp-clip-${el.id}` : null;

    // Text shadow (text elements only — mirrors editor which uses textShadow CSS, not filter)
    const hasShadow = (el.shadowBlur ?? 0) > 0 || (el.shadowOffsetX ?? 0) !== 0 || (el.shadowOffsetY ?? 0) !== 0;
    const shadowColor = hexToRgba(el.shadowColor ?? '#000000', el.shadowOpacity ?? 1);

    // Per-element filter: base filter + effect/shadow presets (matches editor).
    const composed = composeFilter(el);
    const elementFilter = composed && composed !== 'none' ? composed : undefined;

    const baseStyle = {
      position: 'absolute',
      left: el.x,
      top: el.y,
      width: el.width,
      height: el.height,
      transform: `rotate(${el.rotation || 0}deg) scale(${el.flipX ? -1 : 1}, ${el.flipY ? -1 : 1})`,
      opacity: el.opacity ?? 1,
      transformOrigin: 'center center',
      pointerEvents: 'none',
      clipPath: clipId ? `url(#${clipId})` : undefined,
      // Blend mode matches the editor (honored by html2canvas on export too).
      mixBlendMode: blendModeStyle(el),
      boxShadow: shadowBoxCss(el),
      // Only apply element-level image filter here — text shadow is handled per text type below
      filter: elementFilter,
    };

    const clipDef = (clipId && maskEl) ? (
      <svg
        key={`clipdef-${el.id}`}
        style={{ position: 'absolute', width: 0, height: 0, overflow: 'visible', pointerEvents: 'none' }}
      >
        <defs>
          <clipPath id={clipId}>
            {getClipShape(maskEl, el)}
          </clipPath>
        </defs>
      </svg>
    ) : null;

    switch (el.type) {

      // ── Image ─────────────────────────────────────────────────────────────
      // Shared image layout with CanvasEditor (imageImgStyle / imageBoxRadius).
      case 'image': {
        return (
          <div key={el.id} data-thumb-el={el.id} style={{ ...baseStyle, overflow: 'hidden', borderRadius: imageBoxRadius(el) }}>
            {clipDef}
            <img src={el.src} alt="" style={imageImgStyle(el)} />
          </div>
        );
      }

      // ── Frame ─────────────────────────────────────────────────────────────
      case 'frame': {
        const tClip = getFrameClip(el);
        const tClipId = `tp-frame-clip-${el.id}`;
        const tImg = el.image;
        const tLayout = tImg ? computeFrameImageLayout(el, tImg.naturalWidth, tImg.naturalHeight) : null;
        return (
          <div key={el.id} style={{ ...baseStyle }}>
            <svg style={{ position: 'absolute', width: 0, height: 0, overflow: 'visible', pointerEvents: 'none' }}>
              <defs>
                <clipPath id={tClipId}>
                  <path d={tClip.path} transform={`scale(${el.width / tClip.vbW} ${el.height / tClip.vbH})`} />
                </clipPath>
              </defs>
            </svg>
            <div style={{ position: 'absolute', inset: 0, clipPath: `url(#${tClipId})`, overflow: 'hidden', background: tImg ? undefined : '#e5e7eb' }}>
              {tImg && (
                <img
                  src={tImg.src}
                  alt=""
                  style={{
                    position: 'absolute',
                    left: tLayout.left, top: tLayout.top,
                    width: tLayout.width, height: tLayout.height,
                    transform: `rotate(${tImg.rotation || 0}deg)`,
                    maxWidth: 'none', display: 'block',
                  }}
                />
              )}
            </div>
          </div>
        );
      }

      // ── Text ──────────────────────────────────────────────────────────────
      case 'text': {
        // Shared render tree with CanvasEditor's static text — the thumbnail is
        // a scaled copy of the exact same component (textVisualStyle + the
        // StaticTextBlock content), so they cannot drift. `overflow:visible`
        // mirrors the editor (the element box never clips its own text; the page
        // clips). idScope keeps SVG gradient ids unique vs the editor instance.
        return (
          <div key={el.id} data-thumb-el={el.id} style={{ ...baseStyle, overflow: 'visible', ...textVisualStyle(el) }}>
            {clipDef}
            <StaticTextBlock element={el} idScope="tp-" />
          </div>
        );
      }

      // ── Shape ─────────────────────────────────────────────────────────────
      case 'shape': {
        const w = el.width;
        const h = el.height;
        const sw = Number(el.strokeWidth) || 0;
        const half = sw / 2;
        const fillGradId   = `tp-sg-fill-${el.id}`;
        const strokeGradId = `tp-sg-stk-${el.id}`;
        const hasGradFill   = el.fillGradient && el.fillGradient.colors?.length > 0;
        const hasGradStroke = el.strokeGradient && el.strokeGradient.colors?.length > 0;
        const fill   = hasGradFill   ? `url(#${fillGradId})`   : (el.fill || el.fillColor || '#6366f1');
        const stroke = hasGradStroke ? `url(#${strokeGradId})` : (el.stroke || 'none');

        // All shapes rendered as SVG — mirrors CanvasEditor shape rendering
        const cx = w / 2, cy = h / 2;
        const sharedProps = {
          fill,
          stroke,
          strokeWidth: sw,
          strokeLinejoin: 'round',
          fillOpacity: el.fillOpacity ?? 1,
          strokeOpacity: el.strokeOpacity ?? 1,
        };
        let svgShape;
        switch (el.shape) {
          case 'circle':
            svgShape = <ellipse cx={cx} cy={cy} rx={cx - half} ry={cy - half} {...sharedProps} />;
            break;
          case 'roundedRect': {
            const r = Math.min(Number(el.cornerRadius) || 8, cx, cy);
            svgShape = <rect x={half} y={half} width={w - sw} height={h - sw} rx={r} ry={r} {...sharedProps} />;
            break;
          }
          case 'triangle':
            svgShape = <polygon points={`${cx},${half} ${half},${h - half} ${w - half},${h - half}`} {...sharedProps} />;
            break;
          case 'diamond':
            svgShape = <polygon points={`${cx},${half} ${w - half},${cy} ${cx},${h - half} ${half},${cy}`} {...sharedProps} />;
            break;
          case 'hexagon': {
            const r2 = Math.min(cx, cy) - half;
            const pts = Array.from({ length: 6 }, (_, i) => {
              const a = (i * 60 - 90) * Math.PI / 180;
              return `${cx + r2 * Math.cos(a)},${cy + r2 * Math.sin(a)}`;
            }).join(' ');
            svgShape = <polygon points={pts} {...sharedProps} />;
            break;
          }
          case 'pentagon': {
            const r2 = Math.min(cx, cy) - half;
            const pts = Array.from({ length: 5 }, (_, i) => {
              const a = (i * 72 - 90) * Math.PI / 180;
              return `${cx + r2 * Math.cos(a)},${cy + r2 * Math.sin(a)}`;
            }).join(' ');
            svgShape = <polygon points={pts} {...sharedProps} />;
            break;
          }
          case 'octagon': {
            const r2 = Math.min(cx, cy) - half;
            const pts = Array.from({ length: 8 }, (_, i) => {
              const a = (i * 45 - 90 + 22.5) * Math.PI / 180;
              return `${cx + r2 * Math.cos(a)},${cy + r2 * Math.sin(a)}`;
            }).join(' ');
            svgShape = <polygon points={pts} {...sharedProps} />;
            break;
          }
          case 'star': {
            const outer = Math.min(cx, cy) - half, inner = outer * 0.4;
            let d = '';
            for (let i = 0; i < 10; i++) {
              const r2 = i % 2 === 0 ? outer : inner;
              const a = (i * 36 - 90) * Math.PI / 180;
              d += (i === 0 ? 'M' : 'L') + `${cx + r2 * Math.cos(a)},${cy + r2 * Math.sin(a)}`;
            }
            svgShape = <path d={d + 'Z'} {...sharedProps} />;
            break;
          }
          case 'heart': {
            const d = `M${cx},${h * 0.9} C${w * 0.2},${h * 0.7} ${w * 0.05},${h * 0.5} ${w * 0.05},${h * 0.3} C${w * 0.05},${h * 0.15} ${w * 0.15},${h * 0.05} ${w * 0.275},${h * 0.05} C${w * 0.35},${h * 0.05} ${w * 0.42},${h * 0.1} ${cx},${h * 0.2} C${w * 0.58},${h * 0.1} ${w * 0.65},${h * 0.05} ${w * 0.725},${h * 0.05} C${w * 0.85},${h * 0.05} ${w * 0.95},${h * 0.15} ${w * 0.95},${h * 0.3} C${w * 0.95},${h * 0.5} ${w * 0.8},${h * 0.7} ${cx},${h * 0.9}Z`;
            svgShape = <path d={d} {...sharedProps} />;
            break;
          }
          case 'arrow': {
            const pts = `${0},${h * 0.3} ${w * 0.7},${h * 0.3} ${w * 0.7},${0} ${w},${cy} ${w * 0.7},${h} ${w * 0.7},${h * 0.7} ${0},${h * 0.7}`;
            svgShape = <polygon points={pts} {...sharedProps} />;
            break;
          }
          case 'cross': {
            const bw = w * 0.35, bh = h * 0.35;
            const pts = `${cx - bw / 2},0 ${cx + bw / 2},0 ${cx + bw / 2},${cy - bh / 2} ${w},${cy - bh / 2} ${w},${cy + bh / 2} ${cx + bw / 2},${cy + bh / 2} ${cx + bw / 2},${h} ${cx - bw / 2},${h} ${cx - bw / 2},${cy + bh / 2} 0,${cy + bh / 2} 0,${cy - bh / 2} ${cx - bw / 2},${cy - bh / 2}`;
            svgShape = <polygon points={pts} {...sharedProps} />;
            break;
          }
          case 'parallelogram': {
            const skewX = w * 0.25;
            svgShape = <polygon points={`${skewX},0 ${w},0 ${w - skewX},${h} 0,${h}`} {...sharedProps} />;
            break;
          }
          default: { // rect
            const maxRx = Math.max(0, (w - sw) / 2);
            const maxRy = Math.max(0, (h - sw) / 2);
            const rectR = Math.max(0, Number(el.cornerRadius) || 0);
            const rx = Math.min(rectR, maxRx);
            const ry = Math.min(rectR, maxRy);
            svgShape = <rect x={half} y={half} width={w - sw} height={h - sw} rx={rx} ry={ry} {...sharedProps} />;
          }
        }

        return (
          <div key={el.id} style={{ ...baseStyle }}>
            {clipDef}
            <svg
              width={w} height={h}
              style={{ position: 'absolute', top: 0, left: 0, overflow: 'visible' }}
            >
              {(hasGradFill || hasGradStroke) && (
                <defs>
                  {hasGradFill   && buildSVGGradient(fillGradId,   el.fillGradient)}
                  {hasGradStroke && buildSVGGradient(strokeGradId, el.strokeGradient)}
                </defs>
              )}
              {svgShape}
            </svg>
          </div>
        );
      }

      // ── QR code ───────────────────────────────────────────────────────────
      case 'qrcode': {
        return (
          <div
            key={el.id}
            data-thumb-el={el.id}
            style={{
              ...baseStyle,
              backgroundColor: el.bgColor || '#ffffff',
              padding: '8px',
              borderRadius: '8px',
              boxSizing: 'border-box',
            }}
          >
            {clipDef}
            <StaticQR element={el} />
          </div>
        );
      }

      // ── SVG element ───────────────────────────────────────────────────────
      case 'svg':
        if (el.svgContent) {
          // The wrapper MUST have a definite 100% box (mirrors CanvasEditor's
          // SvgContent `w-full h-full` wrapper). processSvgForRender sets the
          // inner <svg> to width/height 100%, and a percentage height only
          // resolves against a definite parent height — with an auto-height
          // wrapper the svg falls back to its viewBox aspect ratio, so its
          // content (e.g. the dotted separator rules) lands at a different
          // vertical position than the editor and gets clipped.
          return (
            <div key={el.id} data-thumb-el={el.id} style={{ ...baseStyle, overflow: 'hidden' }}>
              {clipDef}
              <div
                style={{ width: '100%', height: '100%' }}
                dangerouslySetInnerHTML={{ __html: processSvgForRender(el) }}
              />
            </div>
          );
        }
        return null;

      default:
        return (
          <div key={el.id} style={{ ...baseStyle, overflow: 'hidden', background: 'rgba(99,102,241,0.2)' }}>
            {clipDef}
          </div>
        );
    }
  };

  // ── Background ────────────────────────────────────────────────────────────
  const bgConfig = page.backgroundConfig;
  const isImageBg = bgConfig?.type === 'image' && bgConfig?.url;
  const isGradBg  = bgConfig?.type === 'gradient' && bgConfig?.gradient;
  const bgStyle   = isImageBg ? '#f5f5f5'
    : isGradBg
      ? (typeof bgConfig.gradient === 'string' ? bgConfig.gradient : buildGradientCss(bgConfig.gradient))
    : (page.background || '#ffffff');

  // ── Outer container style ─────────────────────────────────────────────────
  // When viewBounds extends past the canvas, the outer container is the bbox;
  // canvas bg becomes a positioned layer so bleed areas stay transparent.
  const outerStyle = fillContainer
    ? { position: 'absolute', inset: 0, overflow: 'hidden' }
    : fillWidth
    ? { width: '100%', aspectRatio: `${view.width} / ${view.height}`, overflow: 'hidden', position: 'relative', flexShrink: 0 }
    : { width: thumbWidth, height: thumbHeight, overflow: 'hidden', position: 'relative', flexShrink: 0 };

  return (
    <div ref={containerRef} style={outerStyle} data-thumb-canvas="true">
      {/* key flips when the page's font faces finish loading: any text that was
          laid out during the fallback window is remounted and laid out again
          with the real faces. Without this, Safari/WebKit keeps the fallback
          line-box metrics for already-laid-out text (no reflow on font load),
          leaving thumbnail text anchored lower than the same element in the
          editor. A remount is a full, deterministic layout invalidation — no
          offsets, no compensation. */}
      <div key={fontsReady ? `fonts-ready:${fontSig}` : 'fonts-loading'} style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: view.width,
        height: view.height,
        transform: fillContainer
          ? `translate(${offsetX}px, ${offsetY}px) scale(${scale})`
          : `scale(${scale})`,
        transformOrigin: 'top left',
        // NO `will-change: transform` / GPU-layer promotion here. It was added
        // for supersampled text sharpness in Chromium, but a persistent
        // compositor layer over STATIC content is a paint cache that browsers
        // may never refresh: layers rasterized before the page's webfonts
        // loaded kept painting the fallback-metrics render (every text block
        // anchored lower than the editor) even after layout had long
        // corrected itself, and WebKit could leave promoted thumbnail layers
        // entirely blank after scrolling. Un-promoted content re-rasterizes
        // with normal paint invalidation, so what the user sees is always the
        // current (font-correct) layout — which is the parity that matters.
      }}>
        {/* Canvas background — confined to the canvas region within the view */}
        <div style={{
          position: 'absolute',
          left: -view.left,
          top: -view.top,
          width: canvasSize.width,
          height: canvasSize.height,
          background: bgStyle,
          overflow: 'hidden',
        }}>
          {isImageBg && (
            <div style={{
              position: 'absolute',
              inset: 0,
              backgroundImage: `url(${bgConfig.url})`,
              backgroundSize: bgConfig.size || 'cover',
              backgroundPosition: bgConfig.position || 'center center',
              backgroundRepeat: 'no-repeat',
              opacity: bgConfig.opacity ?? 1,
              filter: bgConfig.blur > 0 ? `blur(${bgConfig.blur}px)` : 'none',
              margin: bgConfig.blur > 0 ? `-${bgConfig.blur * 2}px` : 0,
              width: bgConfig.blur > 0 ? `calc(100% + ${bgConfig.blur * 4}px)` : '100%',
              height: bgConfig.blur > 0 ? `calc(100% + ${bgConfig.blur * 4}px)` : '100%',
            }} />
          )}
        </div>
        {/* Elements — positioned in canvas coords; wrapper shifts them into view space */}
        <div style={{
          position: 'absolute',
          left: -view.left,
          top: -view.top,
        }}>
          {/* Duotone filter defs (parity with the editor) */}
          {elements.map((el) => {
            const t = duotoneTableValues(el);
            if (!t) return null;
            return (
              <svg key={t.id} width="0" height="0" style={{ position: 'absolute' }} aria-hidden="true">
                <filter id={t.id} colorInterpolationFilters="sRGB">
                  <feColorMatrix type="matrix" values="0.33 0.33 0.33 0 0 0.33 0.33 0.33 0 0 0.33 0.33 0.33 0 0 0 0 0 1 0" />
                  <feComponentTransfer>
                    <feFuncR type="table" tableValues={t.r} />
                    <feFuncG type="table" tableValues={t.g} />
                    <feFuncB type="table" tableValues={t.b} />
                  </feComponentTransfer>
                </filter>
              </svg>
            );
          })}
          {elements.map(el => renderThumbElement(el))}
        </div>
      </div>
    </div>
  );
};

export default PageThumbnailPreview;
